import type { ArtifactKind, JobType } from '@detachly/contracts'

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * How long a job has been in flight, for a clock that must keep moving while the step list does
 * not. Seconds under a minute, then minutes, then hours — never a raw millisecond count.
 */
export function formatElapsed(startedAt: string, now: number = Date.now()): string {
  const started = new Date(startedAt).getTime()
  if (!Number.isFinite(started)) return '—'

  const totalSeconds = Math.max(0, Math.floor((now - started) / 1000))
  if (totalSeconds < 60) return `${totalSeconds}s`

  const totalMinutes = Math.floor(totalSeconds / 60)
  if (totalMinutes < 60) return `${totalMinutes} min`

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours < 24) return minutes === 0 ? `${hours}h` : `${hours}h ${minutes} min`

  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return restHours === 0 ? `${days}d` : `${days}d ${restHours}h`
}

/** "in 29 days" / "2 hours ago" — the artifact table shows retention, not a raw timestamp. */
export function formatRelative(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '—'

  const deltaMs = new Date(iso).getTime() - now
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ]
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  for (const [unit, ms] of units) {
    if (Math.abs(deltaMs) >= ms) return formatter.format(Math.round(deltaMs / ms), unit)
  }
  return formatter.format(Math.round(deltaMs / 1000), 'second')
}

const JOB_LABELS: Record<JobType, string> = {
  scan: 'Scan',
  content_pack: 'Content Pack',
  scaffold: 'Scaffold',
  ai_home: 'AI rebuild',
}

export function jobLabel(type: JobType): string {
  return JOB_LABELS[type]
}

const ARTIFACT_LABELS: Record<ArtifactKind, string> = {
  content_pack_zip: 'Content Pack (ZIP)',
  scaffold_zip: 'Scaffold project (ZIP)',
  ai_zip: 'AI rebuild (ZIP)',
  ir_json: 'Project IR (JSON)',
  report_md: 'Report (Markdown)',
  screenshots_zip: 'Screenshots (ZIP)',
  build_log: 'Build log',
}

export function artifactLabel(kind: ArtifactKind): string {
  return ARTIFACT_LABELS[kind]
}

/** Whole dollars when the price is whole, which every package in the catalog is. */
export function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)
}

/** `fr_…6081` — enough to recognise a key, never enough to use one. */
export function maskedKey(last4: string): string {
  return `fr_…${last4}`
}
